import { useEffect, useMemo, useState } from 'react';

import { logger } from '@/utils/logger';

export type WorkerProps = {
  workerUrl: new () => Worker;
  payload: any;
  enabled: boolean;
};

const useWorker = <K extends {}>(
  props: WorkerProps,
): {
  data: K | null;
  isLoading: boolean;
  error: Error | null;
} => {
  const { workerUrl, payload, enabled } = props;
  const [data, setData] = useState<K | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const worker = useMemo(() => {
    try {
      const w = new workerUrl();
      return w;
    } catch (error) {
      logger.error('[useWorker] Failed to create worker:', error);
      setError(
        error instanceof Error ? error : new Error('Failed to create worker'),
      );
      return null;
    }
  }, [workerUrl]);

  useEffect(() => {
    if (!worker) {
      console.warn(
        '[useWorker] Worker is null, skipping message handler setup',
      );
      return;
    }

    worker.onmessage = (e: MessageEvent<K>) => {
      setData(e.data);
      setIsLoading(false);
      setError(null);
    };

    worker.onerror = (e: ErrorEvent) => {
      console.error('[useWorker] Worker error:', e);
      setIsLoading(false);
      setError(new Error(e.message));
    };

    return () => {
      worker.terminate();
    };
  }, [worker]);

  useEffect(() => {
    if (!worker) {
      return;
    }

    if (enabled) {
      setIsLoading(true);
      setError(null);
      worker.postMessage(payload);
    } else {
      setIsLoading(false);
      setData(null);
      setError(null);
    }
  }, [worker, payload, enabled]);

  return { data, isLoading, error };
};

export default useWorker;
